import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { cardSurfaceSx } from '../layout/surface';

export interface StatTile {
  /** Translated label, e.g. "Policen gesamt". */
  readonly label: string;
  /** The figure, already formatted (count, `formatCurrency`). */
  readonly value: ReactNode;
  /** `accent` draws the figure in `primary.main` — the one number the row is about. */
  readonly tone?: 'default' | 'accent';
}

export interface StatTilesProps {
  /** Two to four tiles; they share the row equally. */
  readonly items: readonly StatTile[];
  /** Names the row for assistive technology, e.g. "Kennzahlen". */
  readonly label?: string;
}

/**
 * The KPI row (EIS Design System, prompt 24 Phase 24.3): two to four equal cards, each a 12px
 * secondary label over a 22/600 figure. It shows figures the screen ALREADY has — a tile that
 * needs a fetch of its own does not belong in the row (drop it instead).
 *
 * A definition list, so a screen reader hears "Policen gesamt, 12" as one fact rather than two
 * unrelated strings. The surface is the variant's card (`theme.eis.surface`).
 */
export function StatTiles({ items, label }: StatTilesProps): ReactNode {
  return (
    <Box
      component="dl"
      aria-label={label}
      sx={{
        m: 0,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          md: `repeat(${String(Math.min(Math.max(items.length, 1), 4))}, minmax(0, 1fr))`,
        },
      }}
    >
      {items.map((item) => (
        <Paper
          key={item.label}
          variant="outlined"
          sx={(theme) => ({ px: 2.25, py: 2, minWidth: 0, ...cardSurfaceSx(theme) })}
        >
          <Typography
            component="dt"
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            {item.label}
          </Typography>
          <Typography
            component="dd"
            sx={{
              m: 0,
              mt: 0.5,
              fontSize: '1.375rem',
              lineHeight: 1.25,
              fontWeight: 600,
              color: item.tone === 'accent' ? 'primary.main' : 'text.primary',
            }}
          >
            {item.value}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}
